import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Heart, ArrowRight, Loader2, Shield, Sparkles, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [isNewFounder, setIsNewFounder] = useState(false);
  const [founderSlot, setFounderSlot] = useState<number | null>(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const verify = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('verify-payment', {
          body: { sessionId },
        });

        if (error) throw error;
        if (data?.success) {
          setCreditsAdded(data.credits_added || 0);
          setIsNewFounder(data.is_new_founder || false);
          setFounderSlot(data.founder_slot_number || null);
          setStatus('success');
        } else {
          setStatus('error');
        }
      } catch {
        setStatus('error');
      }
    };

    verify();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4 relative overflow-hidden">
      {/* Celebration particles for founders */}
      <AnimatePresence>
        {isNewFounder && status === 'success' && (
          <>
            {Array.from({ length: 20 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute pointer-events-none"
                initial={{
                  opacity: 0,
                  x: '50%',
                  y: '50%',
                  scale: 0,
                }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  x: `${50 + (Math.random() - 0.5) * 80}%`,
                  y: `${50 + (Math.random() - 0.5) * 80}%`,
                  scale: [0, 1.2, 0.8],
                  rotate: Math.random() * 360,
                }}
                transition={{
                  duration: 2.5 + Math.random() * 1.5,
                  delay: Math.random() * 0.8,
                  ease: 'easeOut',
                }}
              >
                {i % 3 === 0 ? (
                  <Heart className="h-4 w-4 text-primary fill-primary" />
                ) : i % 3 === 1 ? (
                  <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                ) : (
                  <Sparkles className="h-3 w-3 text-primary/70" />
                )}
              </motion.div>
            ))}
          </>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10"
      >
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center space-y-6">
            {status === 'verifying' && (
              <>
                <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto" />
                <h2 className="text-xl font-bold">Confirming your purchase...</h2>
                <p className="text-muted-foreground">Just a moment while we add your credits.</p>
              </>
            )}

            {status === 'success' && isNewFounder && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-5"
              >
                {/* Founder celebration hero */}
                <div className="relative mx-auto w-fit">
                  <motion.div
                    className="absolute inset-0 bg-primary/15 rounded-full blur-2xl scale-[2]"
                    animate={{ opacity: [0.5, 0.8, 0.5], scale: [1.8, 2.2, 1.8] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <motion.div
                    className="relative bg-gradient-to-br from-primary/20 via-primary/10 to-accent/10 p-7 rounded-full border border-primary/20"
                    animate={{ scale: [1, 1.04, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Shield className="h-14 w-14 text-primary" />
                  </motion.div>
                </div>

                {/* Founder badge */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                    <Shield className="h-3.5 w-3.5" />
                    Founding Member #{founderSlot}
                  </span>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="space-y-3"
                >
                  <h2 className="text-2xl font-bold text-foreground">
                    Welcome to the Family{' '}
                    <Heart className="inline h-5 w-5 text-primary align-baseline" fill="currentColor" />
                  </h2>
                  <p className="text-muted-foreground leading-relaxed text-sm max-w-sm mx-auto">
                    You are Founding Member <strong>#{founderSlot}</strong> of Say It NOW. 
                    Your belief from the beginning means everything — you're helping keep this space alive for everyone.
                  </p>
                </motion.div>

                {/* Bonus credits notice */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  className="bg-primary/5 border border-primary/15 rounded-xl p-4 space-y-1"
                >
                  <p className="text-sm font-semibold text-foreground flex items-center justify-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-primary" />
                    {creditsAdded + 3} Announcements Ready
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {creditsAdded} from your plan + 3 bonus as our thank you
                  </p>
                </motion.div>

                {/* What's unlocked */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.1 }}
                  className="text-left space-y-2 px-2"
                >
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">As a Founding Member, you get:</p>
                  {[
                    'Locked-in founder pricing forever',
                    'Founding Member badge on your messages',
                    'Your name on the Founders Wall',
                    'Early access to new features',
                    'Direct input on what we build next',
                  ].map((perk, i) => (
                    <motion.div
                      key={perk}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.2 + i * 0.1 }}
                      className="flex items-center gap-2 text-sm text-foreground"
                    >
                      <CheckCircle className="h-3.5 w-3.5 text-primary shrink-0" />
                      {perk}
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.8 }}
                  className="flex flex-col gap-3 pt-2"
                >
                  <Link to="/dashboard">
                    <Button className="w-full gap-2">
                      <Heart className="h-4 w-4" />
                      Go to Your Dashboard
                    </Button>
                  </Link>
                  <Link to="/create">
                    <Button variant="outline" className="w-full gap-2">
                      Write Your First Message
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            )}

            {status === 'success' && !isNewFounder && (
              <>
                <div className="relative mx-auto w-fit">
                  <div className="absolute inset-0 bg-primary/10 rounded-full blur-xl scale-150" />
                  <div className="relative bg-gradient-to-br from-primary/20 to-primary/5 p-6 rounded-full">
                    <CheckCircle className="h-12 w-12 text-primary" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-foreground">You're all set!</h2>
                <p className="text-muted-foreground">
                  {creditsAdded > 0
                    ? `${creditsAdded} announcements have been added to your account.`
                    : 'Your purchase has been confirmed.'}
                </p>
                <div className="flex flex-col gap-3 pt-4">
                  <Link to="/dashboard">
                    <Button className="w-full gap-2">
                      <Heart className="h-4 w-4" />
                      Go to Dashboard
                    </Button>
                  </Link>
                  <Link to="/create">
                    <Button variant="outline" className="w-full gap-2">
                      Create an Announcement
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <h2 className="text-xl font-bold text-foreground">Something went wrong</h2>
                <p className="text-muted-foreground">
                  We couldn't verify your payment. If you were charged, your credits will be added shortly.
                </p>
                <Link to="/dashboard">
                  <Button className="w-full">Return to Dashboard</Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
